import { AppPageNoMaxWidth } from "layouts";
import { ActivityLibraryDataTable } from "./components/ActivityLibraryTable";
import { ActivitySidebar } from "./components/Sidebar";
import { useQuery } from "@apollo/client";
import { CognitiveDomain, StatusView, VoiceMessage } from "../../types/api";
import { useEffect, useState, useMemo } from "react";
import { ActivityDetailModal } from "./components/ActivityDetailModal";
import { convertStringToDate, transformDate } from "utils/date-utils";
import { GET_ALL_ACTIVITIES } from "./queries";
import { DataRow } from "./components/ActivityLibraryTable/types";
import { convertActivationStatus } from "transformers/account.transformer";
import { getEnumName } from "types/utils";
import { ActivityUpload } from "./components/ActivityUpload";
import { differenceInDays } from "date-fns";
import { useSpecialistDropdown } from "hooks/useSpecialistDropDown";

type AllActivitiesResponse = {
  voiceMessages: { count: number; data: VoiceMessage[] };
};

export const ActivityLibrary = () => {
  const [filterCogDomain, setfilterCogDomain] = useState<
    CognitiveDomain | undefined
  >(undefined);
  const [filterCategory, setfilterCategory] = useState<string | undefined>(
    undefined
  );
  const [activityFilterStatus, setActivityFilterStatus] =
    useState<string>("Active");
  const [difficultyFilter, setDifficultyFilter] = useState<string[]>([]);
  const [activityId, setActivityId] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const handleClose = () => setOpen(false);

  const { options: specialistOptions, loading: loadingSpecialists } =
    useSpecialistDropdown();

  const specialistNameMap = useMemo(() => {
    const map = new Map<number, string>();
    specialistOptions.forEach((specialist) => {
      map.set(specialist.value, specialist.label);
    });
    return map;
  }, [specialistOptions]);

  const getSpecialistName = (id: number | undefined) => {
    if (!id) return "-";
    return specialistNameMap.get(id) || "-";
  };

  const { data, error, loading, refetch } = useQuery<AllActivitiesResponse>(
    GET_ALL_ACTIVITIES,
    {
      fetchPolicy: "network-only",
      variables: {
        inLibrary: true,
        statusView: getEnumName(StatusView, activityFilterStatus),
        cognitiveDomain: getEnumName(CognitiveDomain, filterCogDomain),
      },
    }
  );

  useEffect(() => {
    refetch({ statusView: getEnumName(StatusView, activityFilterStatus) });
  }, [activityFilterStatus]);

  const activityLibrary = data?.voiceMessages?.data ?? [];

  const tableData = useMemo(() => {
    if (loadingSpecialists) return [];

    return activityLibrary.map((activity) => {
      const activityStatus = convertActivationStatus(activity?.deletedAt);
      const createdDate = convertStringToDate(activity?.createdOn);
      const isNew = createdDate
        ? differenceInDays(new Date(), createdDate) <= 14
        : false;
      const recorderName = getSpecialistName(activity?.recordedBy?.id);

      const allFields: string = [
        activity?.title,
        transformDate(activity?.createdOn),
        activity?.feature,
        activity?.cognitiveDomains?.join(" "),
        activityStatus,
        isNew ? "new" : "old",
        recorderName,
        activity?.difficulty,
      ]
        .map((x) => x ?? "")
        .join(" ")
        .toLowerCase();

      return {
        id: activity?.id,
        title: activity?.title,
        dateRecorded: transformDate(activity?.createdOn),
        fullISODate: createdDate?.toISOString() ?? "",
        cognitiveDomains: activity?.cognitiveDomains ?? [],
        status: activityStatus,
        allFields,
        newlyCreated: isNew ? "New" : "",
        recorder: recorderName,
        difficulty: activity?.difficulty,
        feature: activity?.feature,
      };
    });
  }, [activityLibrary, loadingSpecialists, specialistNameMap]);

  const onRowClick = (row: DataRow) => {
    setOpen(true);
    setActivityId(row.id);
  };

  const onActivityCreated = (id: number) => {
    setOpen(true);
    setActivityId(id);
  };

  const isLoading = loading || loadingSpecialists;

  return (
    <AppPageNoMaxWidth>
      <ActivitySidebar
        setCogDomainFilter={setfilterCogDomain}
        setCategoryFilter={setfilterCategory}
      />
      <div style={{ width: "100%" }}>
        <ActivityUpload onActivityCreated={onActivityCreated} />
        <ActivityLibraryDataTable
          categoryFilter={filterCategory}
          tableData={tableData}
          error={error}
          loading={isLoading}
          onRowClick={onRowClick}
          setActivityFilterStatus={setActivityFilterStatus}
          activityFilterStatus={activityFilterStatus}
          difficultyFilter={difficultyFilter}
          setDifficultyFilter={setDifficultyFilter}
        />
      </div>
      <ActivityDetailModal
        setActivityFilterStatus={setActivityFilterStatus}
        activityLibrary={activityLibrary}
        open={open}
        onClose={handleClose}
        activityId={activityId}
      />
    </AppPageNoMaxWidth>
  );
};
